'use client';

/* ──────────────────────────────────────────────
 *  BookingCodesContext — champ « Code promo ou carte cadeau »
 *  et résumé du panier.
 *
 *  Les codes vivent dans la machine (`appliedCodes`) : un code
 *  promo et une carte cadeau au plus, cumulables. Ce contexte
 *  calcule le résumé du panier AVEC eux, pose et retire un code
 *  (résumé du panier avant la pré-réservation, PATCH ensuite)
 *  et porte le message affiché sous le champ. Le champ vit dans
 *  le récap (sidebar, feuille mobile), dès l'étape créneau.
 * ────────────────────────────────────────────── */

import {
  type JSX,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  AppliedCodes,
  BookableSlot,
  BookingAmounts,
  BookingCallbacks,
  BookingLabels,
  CartItem,
  CartSummary,
  MemberAssignment,
  PreReservationResponse,
  VoucherResponse,
} from '../types';
import { resolvePromoErrorMessage, resolvePromoIneligibilityMessage } from '../utils/promo-messages';
import { sameCode } from '../utils/same-code';
import { toSupplementSelectionPayload } from '../utils/supplement-selection';
import { readBookingCartCodes, saveBookingCartCodes, type BookingCartCodes } from '../cart/booking-cart-store';
import { useBooking } from './BookingContext';

const noop = (): void => {
  /* intentional no-op */
};

export interface CodeFeedback {
  type: 'success' | 'error' | 'info';
  message: string;
}

/** Email et téléphone saisis, déjà valides : un code promo est alors tranché tout de suite. */
export interface CodeCustomerContext {
  email?: string;
  phone?: string;
}

/** Recrée la pré-réservation avec ces codes (elle n'est plus modifiable) ; `false` : rien n'a été fait. */
export type RecreatePreReservationWithCodes = (codes: AppliedCodes) => Promise<boolean>;

interface BookingCodesValue {
  codes: AppliedCodes;
  /** Montants du récap : ceux de la pré-réservation dès qu'elle existe, sinon ceux du résumé du panier. */
  amounts: BookingAmounts | undefined;
  /** Pose un code du champ unique ; `true` si un code a été posé. */
  applyCode: (code: string) => Promise<boolean>;
  removeCode: (kind: keyof AppliedCodes) => Promise<void>;
  /** Un code est en cours de vérification : « Continuer » attend, sinon la pré-réservation partirait sans lui. */
  busy: boolean;
  /** Champ figé : pré-réservation en cours de création ou paiement en cours. */
  locked: boolean;
  setLocked: (locked: boolean) => void;
  feedback: CodeFeedback | undefined;
  setCustomerContext: (context: CodeCustomerContext) => void;
  /** Déclaré par l'étape paiement, seule à connaître le formulaire et les créneaux à re-réserver. */
  registerRecreate: (recreate: RecreatePreReservationWithCodes | undefined) => void;
  /** Incrémenté à chaque pose ou retrait accepté sur la pré-réservation (montant du paiement changé). */
  revision: number;
  /**
   * Premier code apporté (lien, panier de la visite) que le serveur n'a pas encore accepté : affiché dans le
   * champ avec son motif de refus, réessayé à chaque changement du panier jusqu'à être accepté ou retiré.
   */
  pendingCode: string | undefined;
  /** Le client retire un code en attente (champ vidé) : il n'est plus réessayé. */
  dismissPendingCode: (code: string) => void;
  /**
   * Résumé du panier en échec avant la pré-réservation (bug 11) : le récap n'affiche ni lignes ni montants
   * périmés, mais un message et « Réessayer ».
   */
  summaryError: boolean;
  /** Relance le résumé du panier en échec. */
  retrySummary: () => void;
}

const BookingCodesCtx = createContext<BookingCodesValue | null>(null);

export function useBookingCodes(): BookingCodesValue {
  const ctx = useContext(BookingCodesCtx);
  if (!ctx) throw new Error('useBookingCodes must be used within BookingCodesProvider');
  return ctx;
}

type CartSummaryItemsPayload = Parameters<BookingCallbacks['onComputeCart']>[0];

/**
 * Articles du panier tels que les attend `cart-summary` : formule, suppléments, personnes, et une fois choisis
 * le début de chaque article et ses membres (lot 4 : une promotion juge chaque ligne sur son créneau et son
 * membre). « Sans préférence » n'envoie aucun membre.
 */
export function toCartSummaryItems(
  cartItems: readonly CartItem[],
  choices: { selectedSlot?: BookableSlot | null; memberAssignments?: readonly MemberAssignment[] } = {},
): CartSummaryItemsPayload {
  return cartItems.map((item, index): CartSummaryItemsPayload[number] => {
    const itemSlot = choices.selectedSlot?.slots?.[index];
    const assignment = choices.memberAssignments?.find((candidate): boolean => candidate.cartIndex === index);
    const explicitMemberIds = (assignment?.memberIds ?? []).filter((id): boolean => id !== undefined);
    const memberIds =
      explicitMemberIds.length > 0
        ? explicitMemberIds
        : assignment?.memberId !== undefined
          ? [assignment.memberId]
          : [];
    return {
      prestation_id: item.prestation.prestation_id,
      quantity: item.quantity,
      selected_price_index: item.selectedPriceIndex,
      ...(item.selectedPrice.price_id ? { price_id: item.selectedPrice.price_id } : {}),
      // Suppléments retenus et quantités choisies au stepper (absentes : une par unité facturée).
      ...toSupplementSelectionPayload(item),
      party_size: item.party_size,
      ...(itemSlot?.slot_start ? { slot_start: itemSlot.slot_start } : {}),
      ...(memberIds.length > 0 ? { team_member_ids: memberIds } : {}),
    };
  });
}

/**
 * Clé d'un résumé de panier : articles, codes, créneau et lieu (l'email et le téléphone n'en changent pas les
 * montants).
 */
const summaryKeyOf = (
  items: CartSummaryItemsPayload,
  codes: AppliedCodes,
  slotStart: string | undefined,
  placeId: number | undefined,
): string => JSON.stringify([items, codes.promo ?? '', codes.voucher ?? '', slotStart ?? '', placeId ?? '']);

const withoutCode = (codes: AppliedCodes, kind: keyof AppliedCodes): AppliedCodes => {
  const next = { ...codes };
  delete next[kind];
  return next;
};

function errorKeyOf(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const maybe = (error as { errorKey?: unknown }).errorKey;
  return typeof maybe === 'string' ? maybe : undefined;
}

/** Phrase serveur destinée au client (`PublicApiError.userMessage`), sinon rien. */
function userMessageOf(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const maybe = (error as { userMessage?: unknown }).userMessage;
  return typeof maybe === 'string' && maybe.length > 0 ? maybe : undefined;
}

/** Refus d'un code par le serveur : libellés carte cadeau, libellés code promo, ou message neutre. */
function codeRefusalMessage(
  errorKey: string | undefined,
  serverMessage: string | undefined,
  labels: BookingLabels,
): string {
  if (errorKey?.startsWith('PRE_RESERVATION_PROMO_')) return resolvePromoErrorMessage(errorKey, serverMessage, labels);
  switch (errorKey) {
    case 'PRE_RESERVATION_VOUCHER_CODE_NOT_FOUND':
      return labels.code_error_not_found;
    case 'PRE_RESERVATION_VOUCHER_NOT_ACTIVE':
      return labels.voucher_error_expired;
    case 'PRE_RESERVATION_VOUCHER_NOT_APPLICABLE':
    case 'PRE_RESERVATION_VOUCHER_NO_LONGER_AVAILABLE':
      return labels.voucher_error_invalid;
    default:
      return serverMessage ?? labels.error_generic;
  }
}

/** Message de pose réussie : remise du code promo, ou montant que paie la carte (signés serveur). */
function appliedMessage(
  kind: keyof AppliedCodes,
  code: string,
  amounts: BookingAmounts | undefined,
  labels: BookingLabels,
  voucherRefundableOnCancel?: boolean,
): string {
  const amount = kind === 'promo' ? amounts?.discount_lines?.[0]?.amount : amounts?.voucher_lines?.[0]?.amount;
  const base = amount
    ? labels.code_applied.replace('{code}', code).replace('{amount}', amount)
    : kind === 'promo'
      ? labels.promo_conditional_notice
      : labels.voucher_info_applied_generic;
  return kind === 'voucher' && voucherRefundableOnCancel === false
    ? `${base} ${labels.voucher_info_no_refund_on_cancel}`
    : base;
}

/** Issue de la pose d'un code sur la pré-réservation ; `kind` : nature du code quand l'hôte l'a apprise. */
type PreReservationCodeOutcome = {
  data?: PreReservationResponse;
  errorKey?: string;
  message?: string;
  kind?: keyof AppliedCodes;
};

/** Codes retenus par une pré-réservation. */
const codesOfPreReservation = (data: PreReservationResponse): AppliedCodes => ({
  ...(data.promo_code ? { promo: data.promo_code } : {}),
  ...(data.voucher_code ? { voucher: data.voucher_code } : {}),
});

/** File des codes en attente complétée de ces codes, dans l'ordre, sans vide ni doublon (la même si rien n'y entre). */
function withQueuedCodes(queue: string[], codes: ReadonlyArray<string | undefined>): string[] {
  const next = [...queue];
  for (const raw of codes) {
    const code = raw?.trim();
    if (code && !next.some((known): boolean => sameCode(known, code))) next.push(code);
  }
  return next.length === queue.length ? queue : next;
}

/** File sans ce code (la même s'il n'y était pas). */
function withoutQueuedCode(queue: string[], code: string): string[] {
  const next = queue.filter((known): boolean => !sameCode(known, code));
  return next.length === queue.length ? queue : next;
}

/** Codes gardés par la visite (panier unique), dans l'ordre où le tunnel les réessaie. */
const storedCodesQueue = (codes: BookingCartCodes | undefined): string[] =>
  withQueuedCodes([], [codes?.promo, codes?.voucher, ...(codes?.pending ?? [])]);

/** Clé des codes écrits dans le panier de la visite : rien n'est réécrit tant qu'ils ne changent pas. */
const storedCodesKey = (codes: BookingCartCodes): string =>
  JSON.stringify([codes.promo ?? '', codes.voucher ?? '', codes.pending ?? []]);

interface BookingCodesProviderProps {
  children: ReactNode;
}

export function BookingCodesProvider({ children }: BookingCodesProviderProps): JSX.Element {
  const {
    ctx,
    send,
    callbacks,
    labels,
    currentStep,
    professionalSlug,
    prefilledVoucherCode,
    prefilledPromoCode,
    whenPreReservationReleased,
    setCheckoutBusy,
  } = useBooking();
  const {
    cartItems,
    appliedCodes,
    selectedSlot,
    selectedPlaceId,
    memberAssignments,
    preReservationUuid,
    preReservationData,
    cartSummary,
  } = ctx;

  const [busy, setBusy] = useState(false);
  const [locked, setLockedState] = useState(false);
  const [feedback, setFeedback] = useState<CodeFeedback | undefined>(undefined);
  const [revision, setRevision] = useState(0);
  const lockedRef = useRef(false);
  const customerContextRef = useRef<CodeCustomerContext>({});
  const recreateRef = useRef<RecreatePreReservationWithCodes | undefined>(undefined);
  /* Clé du résumé de panier déjà en place (posé par l'effet, ou par la pose d'un code) : l'effet ne le
     recalcule pas pour rien. */
  const settledSummaryKeyRef = useRef<string | undefined>(undefined);
  /* Résumé en échec (bug 11) : clé de la demande refusée (le récap le dit tant qu'elle reste la clé courante),
     et compteur d'essais qui relance la même demande (« Réessayer »). */
  const [failedSummaryKey, setFailedSummaryKey] = useState<string | undefined>(undefined);
  const [summaryAttempt, setSummaryAttempt] = useState(0);
  const retrySummary = useCallback((): void => setSummaryAttempt((attempt): number => attempt + 1), []);

  /* Champ figé = pré-réservation en cours de création, confirmation ou paiement : le tunnel le sait aussi, pour ne
     pas libérer une pré-réservation que Stripe est en train de régler si le minuteur expire à ce moment-là. */
  const setLocked = useCallback(
    (value: boolean): void => {
      lockedRef.current = value;
      setLockedState(value);
      setCheckoutBusy(value);
    },
    [setCheckoutBusy],
  );
  const setCustomerContext = useCallback((value: CodeCustomerContext): void => {
    customerContextRef.current = value;
  }, []);
  const registerRecreate = useCallback((recreate: RecreatePreReservationWithCodes | undefined): void => {
    recreateRef.current = recreate;
  }, []);

  /* ── Codes en attente : apportés par le lien (`?code=`, `?promo=`) ou gardés par la visite (panier unique) ──
   * Un code en attente est essayé dès que le panier est prêt, puis réessayé à chaque changement du panier
   * (lignes, créneau, lieu, autres codes) tant que le serveur ne l'accepte pas : un code à minimum d'achat
   * refusé sur une prestation ne l'est peut-être plus une fois la seconde ajoutée. Il n'en sort qu'accepté, ou
   * retiré par le client (champ vidé). Ceux de la visite d'abord, ceux du lien ensuite : à nature égale, le code
   * du lien, choisi en dernier, remplace l'autre. */
  const [pendingCodes, setPendingCodes] = useState<string[]>((): string[] =>
    storedCodesQueue(readBookingCartCodes(professionalSlug)),
  );
  useEffect((): void => {
    if (!prefilledPromoCode?.trim() && !prefilledVoucherCode?.trim()) return;
    setPendingCodes((queue): string[] => withQueuedCodes(queue, [prefilledPromoCode, prefilledVoucherCode]));
  }, [prefilledPromoCode, prefilledVoucherCode]);
  const dismissPendingCode = useCallback((code: string): void => {
    setPendingCodes((queue): string[] => withoutQueuedCode(queue, code));
  }, []);

  /* Le message du champ parle de la dernière pose : il ne suit pas le client quand il quitte une étape où
     le champ était sous ses yeux (créneau, paiement). Celui d'un code apporté par le lien, posé plus tôt,
     attend que le champ apparaisse ; celui d'un code en attente, toujours dans le champ, reste avec lui. */
  const previousStepRef = useRef(currentStep);
  useEffect((): void => {
    const previous = previousStepRef.current;
    previousStepRef.current = currentStep;
    if (previous !== currentStep && (previous === 'slot' || previous === 'checkout') && pendingCodes.length === 0) {
      setFeedback(undefined);
    }
  }, [currentStep, pendingCodes.length]);

  const items = useMemo(
    (): CartSummaryItemsPayload => toCartSummaryItems(cartItems, { selectedSlot, memberAssignments }),
    [cartItems, selectedSlot, memberAssignments],
  );
  const slotStart = selectedSlot?.slot_start;
  // Créneau et lieu tranchent les promotions (happy hours, lieux), automatiques comprises : le résumé suit.
  const summarySlotStart = slotStart;
  const summaryContext = useMemo(
    (): { slot_start?: string; pro_place_id?: number } => ({
      ...(summarySlotStart ? { slot_start: summarySlotStart } : {}),
      ...(selectedPlaceId ? { pro_place_id: selectedPlaceId } : {}),
    }),
    [summarySlotStart, selectedPlaceId],
  );
  const summaryKey = summaryKeyOf(items, appliedCodes, summarySlotStart, selectedPlaceId);

  /* ── Résumé du panier, calculé avec les codes posés (debounce 300 ms) ──
   * Avant la pré-réservation, un code que le panier n'accepte plus (prestation retirée, créneau hors des
   * horaires du code) disparaît, avec un message. Ensuite, la pré-réservation fait foi : seul le code
   * promo accompagne le résumé (badges des lignes) — la carte, débitée par la pré-réservation, y serait
   * vue épuisée. En sortir (retour, « modifier », minuteur expiré) la libère : le résumé repart avec la
   * carte une fois le DELETE fini (`whenPreReservationReleased`, borné), sinon le serveur, qui tient
   * encore le débit et la réservation du code pour elle, les ferait retirer à tort. */
  useEffect((): (() => void) => {
    if (items.length === 0) return noop;
    const preReserved = !!preReservationUuid;
    // Résumé sans la carte pendant la pré-réservation : à recalculer avec elle si on en sort.
    const requestKey = preReserved ? `pre-reserved:${summaryKey}` : summaryKey;
    if (requestKey === settledSummaryKeyRef.current && cartSummary) return noop;

    send({ type: 'CART_SUMMARY_LOADING' });
    // Réponse d'un panier dépassé (modifié entre-temps) : ignorée, sinon elle écraserait celle du
    // panier courant — la machine accepte le résumé à toutes les étapes.
    let stale = false;
    const requestedCodes = appliedCodes;
    const timer = setTimeout((): void => {
      // Libération en cours : le résumé part une fois la carte et le code rendus par le serveur (ou au délai borné).
      whenPreReservationReleased()
        .then(
          (): Promise<CartSummary | undefined> =>
            stale
              ? Promise.resolve(undefined)
              : callbacks.onComputeCart(
                  items,
                  preReserved ? undefined : requestedCodes.voucher,
                  requestedCodes.promo,
                  summaryContext.slot_start || summaryContext.pro_place_id ? summaryContext : undefined,
                ),
        )
        .then((summary: CartSummary | undefined): void => {
          if (stale || !summary) return;
          settledSummaryKeyRef.current = requestKey;
          setFailedSummaryKey(undefined);
          // Backend antérieur au bloc `amounts` : il ne dit pas quels codes il a retenus.
          if (preReserved || !summary.amounts) {
            send({ type: 'CART_SUMMARY_LOADED', summary });
            return;
          }
          const kept: AppliedCodes = {
            ...(requestedCodes.promo && summary.promo_code ? { promo: requestedCodes.promo } : {}),
            ...(requestedCodes.voucher && summary.voucher_code ? { voucher: requestedCodes.voucher } : {}),
          };
          const dropped = [requestedCodes.promo, requestedCodes.voucher].filter(
            (code): code is string => !!code && code !== kept.promo && code !== kept.voucher,
          );
          if (dropped.length === 0) {
            send({ type: 'CART_SUMMARY_LOADED', summary });
            return;
          }
          settledSummaryKeyRef.current = summaryKeyOf(items, kept, summarySlotStart, selectedPlaceId);
          send({ type: 'CODES_CHANGED', codes: kept, summary });
          setFeedback({
            type: 'info',
            message:
              summary.promo_ineligibility_message ??
              (summary.promo_ineligibility_reason && dropped.includes(requestedCodes.promo ?? '')
                ? resolvePromoIneligibilityMessage(summary.promo_ineligibility_reason, labels)
                : labels.code_dropped.replace('{code}', dropped.join(', '))),
          });
        })
        .catch((error: unknown): void => {
          if (stale) return;
          // Carte cadeau refusée pour ce panier : retirée, le résumé repart sans elle.
          if (!preReserved && requestedCodes.voucher && errorKeyOf(error)?.startsWith('PRE_RESERVATION_VOUCHER_')) {
            send({ type: 'CODES_CHANGED', codes: withoutCode(requestedCodes, 'voucher') });
            setFeedback({ type: 'info', message: labels.code_dropped.replace('{code}', requestedCodes.voucher) });
            return;
          }
          // Résumé en échec (bug 11) : le récap le dit et propose « Réessayer », au lieu de garder un total périmé.
          setFailedSummaryKey(requestKey);
          send({ type: 'CART_SUMMARY_FAILED' });
        });
    }, 300);

    return (): void => {
      stale = true;
      clearTimeout(timer);
    };
    // Le résumé déjà en place pour cette clé n'est pas recalculé (cf. `settledSummaryKeyRef`) ; `summaryAttempt`
    // relance une demande en échec.
  }, [
    summaryAttempt,
    summaryKey,
    items,
    appliedCodes,
    summarySlotStart,
    selectedPlaceId,
    summaryContext,
    cartSummary,
    preReservationUuid,
    callbacks,
    send,
    labels,
    whenPreReservationReleased,
  ]);

  /* ── Pose d'un code avant la pré-réservation : résumé du panier avec ce code ── */
  const applyCodeToCart = useCallback(
    async (code: string): Promise<boolean> => {
      let summary: CartSummary;
      try {
        // Code retapé juste après avoir quitté la pré-réservation : le serveur le tient encore pour elle
        // tant que le DELETE n'est pas fini (le champ montre « vérification » pendant ce temps).
        await whenPreReservationReleased();
        const customer = customerContextRef.current;
        summary = await callbacks.onComputeCart(items, appliedCodes.voucher, appliedCodes.promo, {
          code,
          ...(slotStart ? { slot_start: slotStart } : {}),
          ...(selectedPlaceId ? { pro_place_id: selectedPlaceId } : {}),
          ...(customer.email ? { email: customer.email } : {}),
          ...(customer.phone ? { phone: customer.phone } : {}),
        });
      } catch (error: unknown) {
        setFeedback({ type: 'error', message: codeRefusalMessage(errorKeyOf(error), userMessageOf(error), labels) });
        return false;
      }
      // La pré-réservation est partie pendant la vérification, sans ce code : à ré-appliquer.
      if (lockedRef.current) {
        setFeedback({ type: 'error', message: labels.voucher_error_retry });
        return false;
      }
      const kind: keyof AppliedCodes | undefined = sameCode(summary.promo_code, code)
        ? 'promo'
        : sameCode(summary.voucher_code, code)
          ? 'voucher'
          : undefined;
      if (!kind) {
        // Refusé : le récap garde les codes posés avant (la réponse a calculé sans eux).
        setFeedback({
          type: 'error',
          message: summary.promo_ineligibility_reason
            ? (summary.promo_ineligibility_message ??
              resolvePromoIneligibilityMessage(summary.promo_ineligibility_reason, labels))
            : labels.code_error_not_found,
        });
        return false;
      }
      const codes: AppliedCodes = {
        ...(summary.promo_code ? { promo: summary.promo_code } : {}),
        ...(summary.voucher_code ? { voucher: summary.voucher_code } : {}),
      };
      settledSummaryKeyRef.current = summaryKeyOf(items, codes, slotStart, selectedPlaceId);
      setFailedSummaryKey(undefined);
      send({ type: 'CODES_CHANGED', codes, summary });
      setFeedback({ type: 'success', message: appliedMessage(kind, codes[kind] ?? code, summary.amounts, labels) });
      return true;
    },
    [callbacks, items, appliedCodes, slotStart, selectedPlaceId, labels, send, whenPreReservationReleased],
  );

  /* Pré-réservation devenue non modifiable : pré-réservation recréée par l'étape paiement avec les codes
     voulus. La nature du code, quand l'hôte ne l'a pas apprise, vient du résumé du panier. */
  const recreateWithCode = useCallback(
    async (code: string, knownKind?: keyof AppliedCodes): Promise<boolean> => {
      const recreate = recreateRef.current;
      if (!recreate) return false;
      let kind = knownKind;
      if (!kind) {
        const summary = await callbacks.onComputeCart(items, undefined, undefined, {
          code,
          ...(slotStart ? { slot_start: slotStart } : {}),
        });
        kind = sameCode(summary.promo_code, code)
          ? 'promo'
          : sameCode(summary.voucher_code, code)
            ? 'voucher'
            : undefined;
      }
      if (!kind) return false;
      return recreate({ ...appliedCodes, [kind]: code });
    },
    [callbacks, items, slotStart, appliedCodes],
  );

  /* Hôte sans `onApplyCode` : la carte cadeau d'abord, puis le code promo si le code n'en est pas une. */
  const applyCodeLegacy = useCallback(
    async (uuid: string, code: string): Promise<PreReservationCodeOutcome> => {
      if (!callbacks.onApplyVoucher) return {};
      const voucher: VoucherResponse = await callbacks.onApplyVoucher(uuid, code);
      if (voucher.valid) return { data: voucher.updated_pre_reservation };
      const notAVoucher =
        voucher.error_key === 'PRE_RESERVATION_VOUCHER_CODE_NOT_FOUND' ||
        (voucher.error_key === undefined && voucher.status === 400);
      if (!notAVoucher || !callbacks.onApplyPromo) {
        return { errorKey: voucher.error_key, message: voucher.message, kind: 'voucher' };
      }
      const promo = await callbacks.onApplyPromo(uuid, code);
      return promo.valid
        ? { data: promo.updated_pre_reservation }
        : { errorKey: promo.error_key, message: promo.message, kind: 'promo' };
    },
    [callbacks],
  );

  /* ── Pose d'un code sur la pré-réservation ── */
  const applyCodeToPreReservation = useCallback(
    async (uuid: string, code: string): Promise<boolean> => {
      let outcome: PreReservationCodeOutcome;
      try {
        if (callbacks.onApplyCode) {
          const result = await callbacks.onApplyCode(uuid, code);
          outcome = result.valid
            ? { data: result.updated_pre_reservation }
            : { errorKey: result.error_key, message: result.message };
        } else {
          outcome = await applyCodeLegacy(uuid, code);
        }
      } catch {
        setFeedback({ type: 'error', message: labels.error_generic });
        return false;
      }

      if (outcome.data) {
        const codes = codesOfPreReservation(outcome.data);
        const kind: keyof AppliedCodes = sameCode(codes.promo, code) ? 'promo' : 'voucher';
        send({ type: 'PRE_RESERVATION_CREATED', data: outcome.data });
        setRevision((value): number => value + 1);
        setFeedback({
          type: 'success',
          message: appliedMessage(
            kind,
            codes[kind] ?? code,
            outcome.data.amounts,
            labels,
            outcome.data.voucher_refundable_on_cancel,
          ),
        });
        return true;
      }

      if (outcome.errorKey === 'PRE_RESERVATION_NOT_EDITABLE') {
        try {
          if (await recreateWithCode(code, outcome.kind)) {
            setRevision((value): number => value + 1);
            setFeedback({ type: 'success', message: labels.voucher_info_applied_generic });
            return true;
          }
        } catch {
          setFeedback({ type: 'error', message: labels.error_generic });
          return false;
        }
      }
      setFeedback({ type: 'error', message: codeRefusalMessage(outcome.errorKey, outcome.message, labels) });
      return false;
    },
    [callbacks, applyCodeLegacy, recreateWithCode, labels, send],
  );

  const applyCode = useCallback(
    async (rawCode: string): Promise<boolean> => {
      const code = rawCode.trim();
      if (!code) {
        setFeedback({ type: 'error', message: labels.voucher_error_empty });
        return false;
      }
      if (lockedRef.current) return false;
      setBusy(true);
      setFeedback(undefined);
      try {
        const applied = preReservationUuid
          ? await applyCodeToPreReservation(preReservationUuid, code)
          : await applyCodeToCart(code);
        // Accepté (automatiquement ou par le client) : il n'est plus en attente.
        if (applied) setPendingCodes((queue): string[] => withoutQueuedCode(queue, code));
        return applied;
      } finally {
        setBusy(false);
      }
    },
    [labels.voucher_error_empty, preReservationUuid, applyCodeToPreReservation, applyCodeToCart],
  );

  const removeCode = useCallback(
    async (kind: keyof AppliedCodes): Promise<void> => {
      if (lockedRef.current || !appliedCodes[kind]) return;
      setBusy(true);
      setFeedback(undefined);
      try {
        if (preReservationUuid) {
          const remove = kind === 'promo' ? callbacks.onRemovePromo : callbacks.onRemoveVoucher;
          const result = await remove?.(preReservationUuid);
          if (result?.updated_pre_reservation) {
            send({ type: 'PRE_RESERVATION_CREATED', data: result.updated_pre_reservation });
          } else {
            send({ type: 'CODES_CHANGED', codes: withoutCode(appliedCodes, kind) });
          }
          setRevision((value): number => value + 1);
        } else {
          send({ type: 'CODES_CHANGED', codes: withoutCode(appliedCodes, kind) });
        }
      } catch {
        setFeedback({ type: 'error', message: labels.error_generic });
      } finally {
        setBusy(false);
      }
    },
    [appliedCodes, preReservationUuid, callbacks, send, labels.error_generic],
  );

  /* ── Essai des codes en attente : dès que le panier est prêt, puis à chaque changement du panier ──
   * Un code par passage (la pose suivante part avec les codes que la précédente a fait retenir), et un code
   * refusé n'est pas réessayé tant que le panier (`summaryKey` : lignes, créneau, lieu, codes posés) ne change
   * pas. Jamais pendant une vérification ni quand le champ est figé. */
  const triedRef = useRef<{ signature: string; codes: string[] }>({ signature: '', codes: [] });
  const applyingRef = useRef(false);
  useEffect((): void => {
    if (ctx.dataLoading || cartItems.length === 0 || busy || locked || applyingRef.current) return;
    if (pendingCodes.length === 0) return;
    if (triedRef.current.signature !== summaryKey) triedRef.current = { signature: summaryKey, codes: [] };
    const tried = triedRef.current.codes;
    const next = pendingCodes.find((code): boolean => !tried.some((known): boolean => sameCode(known, code)));
    if (next === undefined) return;
    tried.push(next);
    // Déjà posé (pastille) : rien à essayer.
    if (sameCode(appliedCodes.promo, next) || sameCode(appliedCodes.voucher, next)) {
      setPendingCodes((queue): string[] => withoutQueuedCode(queue, next));
      return;
    }
    applyingRef.current = true;
    void applyCode(next).finally((): void => {
      applyingRef.current = false;
    });
  }, [ctx.dataLoading, cartItems.length, busy, locked, pendingCodes, summaryKey, appliedCodes, applyCode]);

  /* ── Codes gardés par la visite (panier unique) : posés et en attente, à chaque changement ──
   * La barre de panier du site chiffre son total avec eux et le tunnel les reprend s'il rouvre pendant la
   * visite. Réservation confirmée : le panier est vidé par le tunnel, codes compris. */
  const savedCodesKeyRef = useRef<string | undefined>(undefined);
  useEffect((): void => {
    if (!professionalSlug || currentStep === 'success') return;
    const codes: BookingCartCodes = { ...appliedCodes, pending: pendingCodes };
    const key = storedCodesKey(codes);
    if (key === savedCodesKeyRef.current) return;
    savedCodesKeyRef.current = key;
    saveBookingCartCodes(professionalSlug, codes);
  }, [professionalSlug, currentStep, appliedCodes, pendingCodes]);

  const amounts = preReservationData?.amounts ?? cartSummary?.amounts;
  const pendingCode = pendingCodes[0];
  // Après la pré-réservation, ses montants font foi : un résumé en échec n'y change rien d'affiché.
  const summaryError = !preReservationUuid && failedSummaryKey === summaryKey;

  const value = useMemo(
    (): BookingCodesValue => ({
      codes: appliedCodes,
      amounts,
      applyCode,
      removeCode,
      busy,
      locked,
      setLocked,
      feedback,
      setCustomerContext,
      registerRecreate,
      revision,
      pendingCode,
      dismissPendingCode,
      summaryError,
      retrySummary,
    }),
    [
      appliedCodes,
      amounts,
      applyCode,
      removeCode,
      busy,
      locked,
      setLocked,
      feedback,
      setCustomerContext,
      registerRecreate,
      revision,
      pendingCode,
      dismissPendingCode,
      summaryError,
      retrySummary,
    ],
  );

  return <BookingCodesCtx.Provider value={value}>{children}</BookingCodesCtx.Provider>;
}
